import { OrderDetailsItem, OrderRecapRawData } from '@/services/innovorder/orders/order.type';
import { IntlShape } from 'react-intl';
import { computeCurrency } from '@/locales/utils';

export type OrderDetailsItemWithCustomization = OrderDetailsItem & { customization: OrderDetailsItem[] };

const isNotCancelled = (item: OrderDetailsItem, isContraEntry: boolean): boolean => {
    if (isContraEntry) {
        return item.status !== 'CANCELLED';
    }
    return !item.cancelReason;
};

export const unflattenItems = (
    items: OrderDetailsItem[],
    isContraEntry: boolean,
): OrderDetailsItemWithCustomization[] => {
    const outputItems: OrderDetailsItemWithCustomization[] = [];
    if (!items) return outputItems;

    items.forEach((item) => {
        if (item.itemType !== 'CUSTOMIZATION') {
            outputItems.push({ ...item, customization: [] });
        } else if (isNotCancelled(item, isContraEntry)) {
            outputItems[outputItems.length - 1].customization.push({ ...item });
            if (!item.free && item.totalPriceWithTaxIncluded) {
                outputItems[outputItems.length - 1].totalPriceWithTaxIncluded += item.totalPriceWithTaxIncluded;
            }
        }
    });
    return outputItems.filter((item) => isNotCancelled(item, isContraEntry));
};

export const calculateSum = (table: Array<{ amount: number }>): number =>
    table
        ? table.reduce((sum, line) => {
              sum += line.amount;
              return sum;
          }, 0)
        : 0;

export const getTaxLetter = (tax: number, taxes?: number[]): string => {
    if (!tax || !taxes || taxes.length < 2) return '';

    const taxIndex = taxes.findIndex((taxValue) => taxValue === tax);

    if (taxIndex < 0) return '';

    const taxLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (taxIndex >= taxLetters.length) return '*';

    return taxLetters[taxIndex];
};

export const computeProductItemPrice = (item: OrderDetailsItemWithCustomization): number => {
    if (!item.free) {
        return item.totalPriceWithTaxIncluded;
    }

    if (item.customization && item.customization.length > 0) {
        return item.totalPriceWithTaxIncluded;
    }

    return 0;
};

export const getLoyaltyDiscounts = (orderRecapRawData: string | null) => {
    if (!orderRecapRawData) {
        return [];
    }
    try {
        const parsedOrderRecapRawData = JSON.parse(orderRecapRawData) as OrderRecapRawData;
        return parsedOrderRecapRawData.discounts?.filter((discount) => discount.origin?.type === 'LOYALTY') || [];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing order recap raw data');
        return [];
    }
};

export type AggregatedCustomization = { itemId: number; name: string; quantity: number };

// Customizations are returned by the API in two shapes depending on the channel:
//  - one row aggregated (quantity already reflects total)
//  - one row per parent instance (quantity reflects parent multiplier)
// We group by itemId and divide by the parent quantity to always get the
// per-parent count, so display is consistent (e.g. "3x yahourt" for both
// `1x menu (3 yahourts grouped)` and `2x menu (3 yahourt rows of qty 2)`).
export const aggregateCustomizations = (
    customizations: OrderDetailsItem[],
    parentQuantity: number,
): AggregatedCustomization[] => {
    // Group by name: rows of the same customization can share a name but have
    // different itemIds (one DB row per parent instance), and conversely two
    // distinct customizations may share an itemId but differ by name. Name is
    // what the user actually reads, so use it as the merge key.
    const grouped = new Map<string, AggregatedCustomization>();
    customizations.forEach((c) => {
        const existing = grouped.get(c.name);
        if (existing) {
            existing.quantity += c.quantity;
        } else {
            grouped.set(c.name, { itemId: c.itemId, name: c.name, quantity: c.quantity });
        }
    });
    // Contra-entry orders carry negative quantities; use absolute value so
    // the divisor is the parent count, not 1.
    const divisor = Math.abs(parentQuantity) || 1;
    return Array.from(grouped.values()).map((g) => ({
        ...g,
        quantity: Math.round(g.quantity / divisor),
    }));
};

export const formatMeasurementUnitText = (item: OrderDetailsItemWithCustomization, intl: IntlShape): string => {
    if (!item.measuredQuantity || item.measuredQuantity === 0) return '';
    const unitPrice = computeProductItemPrice(item) / item.measuredQuantity;
    const formattedUnitPrice = computeCurrency({ intl, amount: unitPrice });
    const formattedQuantity = item.measuredQuantity.toFixed(3);
    return `${formattedQuantity}${item.measurementUnit} x ${formattedUnitPrice}/${item.measurementUnit}`;
};
